import styled from 'styled-components';

const WIDTH = 120;
const WIDTH_CLEARABLE = 140;
const WIDTH_WITH_TIME = 160;
const WIDTH_WITH_TIME_CLEARABLE = 180;

export const StyledDatepicker = styled.div<{
  isBlock: boolean;
  isClearable: boolean;
  isSmall: boolean;
  withTime: boolean;
}>(
  ({
    isBlock,
    isClearable,
    isSmall,
    withTime,

    theme: { colors, fonts, datePicker },
  }) => ({
    display: isBlock ? 'block' : 'inline-block',
    position: 'relative',
    width: isBlock
      ? 'auto'
      : withTime
        ? isClearable
          ? WIDTH_WITH_TIME_CLEARABLE
          : WIDTH_WITH_TIME
        : isClearable
          ? WIDTH_CLEARABLE
          : WIDTH,

    '& > div:empty': {
      display: 'none'
    },

    '& .react-datepicker-wrapper': {
      display: 'block',
    },

    '& .react-datepicker': {
      width: withTime ? datePicker.CALENDAR_WITH_TIME_WIDTH : undefined,
      backgroundColor: colors.NEUTRAL_WHITE,
      color: colors.TEXT_PRIMARY,
      borderColor: colors[datePicker.CALENDAR_BORDER_COLOR],
      boxShadow: datePicker.CALENDAR_SHADOW,
      padding: datePicker.CALENDAR_PADDING,
      minHeight: datePicker.CALENDAR_MIN_HEIGHT,
    },

    '& .react-datepicker__input-container': {
      display: 'block',
      width: '100%',
    },

    '& .react-datepicker__triangle': {
      display: 'none',
    },

    '& .react-datepicker__close-icon': {
      width: 16,
      height: 16,
      top: isSmall ? 5 : 10,
      right: 30,
      padding: 0,

      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        backgroundColor: colors[datePicker.CALENDAR_ICON_COLOR],
        borderRadius: 0,
        width: 10,
        height: 1,
        top: 10,
        left: 3,
        margin: 0,
        padding: 0,
        transition: 'background-color 150ms ease 0s',
      },

      '&::before': {
        transform: 'rotate(47deg)',
      },

      '&::after': {
        transform: 'rotate(-47deg)',
      },

      '&:hover': {
        '&::before, &::after': {
          backgroundColor: colors[datePicker.CALENDAR_ICON_HOVER_COLOR],
        },
      },

      '&:active': {
        '&::before, &::after': {
          backgroundColor: colors[datePicker.CALENDAR_ICON_ACTIVE_COLOR],
        },
      },
    },

    '& .react-datepicker__month': {
      margin: 0,
      padding: '0.4rem',
    },

    '& .react-datepicker__current-month, & .react-datepicker-time__header': {
      ...fonts.DEFAULT,
      color: colors.NEUTRAL_DEFAULT,
    },

    '& .react-datepicker__header__dropdown': {
      gridArea: 'b',
      textAlign: 'left',
    },

    '& .react-datepicker__day-names': {
      gridArea: 'c',
    },

    '& .react-datepicker__year-dropdown-container': {
      margin: 0,
    },

    '& .react-datepicker__year-read-view': {
      display: 'inline-flex',
      alignItems: 'center',
      flexDirection: 'row-reverse',
    },

    '& .react-datepicker__year-read-view--selected-year': {
      padding: datePicker.CALENDAR_SELECTED_YEAR_PADDING,
      borderRadius: 0,
      background: colors[datePicker.CALENDAR_SELECTED_YEAR_BG_COLOR],
    },

    '& .react-datepicker__year-read-view--down-arrow': {
      width: 16,
      height: 16,
      border: 'none',
      top: 0,
      left: 0,
      transform: 'rotate(0deg)',
      backgroundImage: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNyIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE3IDE2IiBmaWxsPSJub25lIj4NCjxwYXRoIGQ9Ik0xMi41IDEyTDguNSAxNkw0LjUgMTJIMTIuNVoiIGZpbGw9IiMxNjE2MTYiLz4NCjxwYXRoIGQ9Ik00LjUgNEw4LjUgMEwxMi41IDRINC41WiIgZmlsbD0iIzE2MTYxNiIvPg0KPC9zdmc+")',
      position: 'static',
    },

    '& .react-datepicker__year-dropdown': {
      backgroundColor: colors.NEUTRAL_WHITE,
      borderColor: colors[datePicker.CALENDAR_BORDER_COLOR],
      boxShadow: datePicker.CALENDAR_SHADOW,
    },

    '& .react-datepicker__year-dropdown .react-datepicker__year-option': {
      width: 'auto',
      lineHeight: '28px',
      color: colors.TEXT_PRIMARY,
    },

    '& .react-datepicker__year-option.react-datepicker__year-option--selected_year': {
      color: colors.ACTION_DEFAULT,
    },

    '& .react-datepicker__year-option:hover, .react-datepicker__month-option:hover, .react-datepicker__month-year-option:hover': {
      background: colors.NEUTRAL_BACKGROUND,
    },

    '& .react-datepicker__year-option--selected, .react-datepicker__month-option--selected, .react-datepicker__month-year-option--selected': {
      display: 'none',
    },

    '& .react-datepicker__time-container': {
      '& .react-datepicker__time': {
        backgroundColor: colors.NEUTRAL_WHITE,

        '& .react-datepicker__time-box': {
          'ul.react-datepicker__time-list': {
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: '0.4rem',
            width: '100%',

            'li.react-datepicker__time-list-item': {
              ...fonts.CAPTION,

              '&:hover': {
                backgroundColor: colors.NEUTRAL_BACKGROUND,
              },

              '&.react-datepicker__time-list-item--selected': {
                backgroundColor: colors.ACTION_DEFAULT,
                color: colors.NEUTRAL_WHITE,
              },
            },
          },
        },
      },
    },

    '& .react-datepicker__header': {
      ...fonts.DEFAULT,
      backgroundColor: colors.NEUTRAL_WHITE,
      borderBottom: 'none',
      display: 'grid',
      gridTemplateAreas: `"a b"
                          "c c"`,
      gridTemplateColumns: '1f 1fr',
      gridColumnGap: 8,
    },

    '& .react-datepicker__current-month': {
      gridArea: 'a',
      color: colors.TEXT_PRIMARY,
      height: datePicker.CALENDAR_CURRENT_MONTH_HEIGHT,
      textAlign: 'right',
      lineHeight: '30px',
    },

    '& .react-datepicker__day-name, & .react-datepicker__day, & .react-datepicker__time-name':
      {
        ...fonts.DEFAULT,
        width: datePicker.CALENDAR_DAY_SIZE,
        lineHeight: datePicker.CALENDAR_DAY_SIZE,
        color: colors.TEXT_PRIMARY,
        margin: datePicker.CALENDAR_DAY_MARGIN,
      },

    '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected, & .react-datepicker__day:hover':
      {
        borderRadius: 4,
      },

    '&.react-datepicker__day--outside-month': {
      color: colors[datePicker.CALENDAR_DAY_OUTSIDE_MONTH_COLOR],

      '&.react-datepicker__day--selected, &.react-datepicker__day--keyboard-selected':
        {
          color: colors.NEUTRAL_WHITE,
        },
    },

    '& .react-datepicker__day--disabled': {
      color: colors[datePicker.CALENDAR_DAY_DISABLED_COLOR],
    },

    '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected':
      {
        color: colors.NEUTRAL_WHITE,
      },

    '& .react-datepicker__day:hover': {
      backgroundColor: colors.NEUTRAL_BACKGROUND,
    },

    '& .react-datepicker__day:focus': {
      backgroundColor: colors.NEUTRAL_BACKGROUND,
      outline: 'none',
    },

    '& .react-datepicker__day--keyboard-selected': {
      backgroundColor: colors.ACTION_PRESSED,

      '&:hover': {
        backgroundColor: colors.ACTION_PRESSED,
      },
    },

    '& .react-datepicker__day--today': {
      position: 'relative',
      color: colors.ACTION_DEFAULT,

      '&::after': {
        content: '""',
        position: 'absolute',
        left: '50%',
        marginLeft: -2,
        bottom: datePicker.CALENDAR_DAY_TODAY_BOTTOM,
        width: datePicker.CALENDAR_DAY_TODAY_SIZE,
        height: datePicker.CALENDAR_DAY_TODAY_SIZE,
        borderRadius: datePicker.CALENDAR_DAY_TODAY_BORDER_RADIUS,
        backgroundColor: colors.ACTION_DEFAULT,
      },

      '&.react-datepicker__day--selected, &.react-datepicker__day--keyboard-selected':
        {
          '&::after': {
            backgroundColor: colors.NEUTRAL_WHITE,
          },
        },
    },

    '& .react-datepicker__day--selected': {
      backgroundColor: colors[datePicker.CALENDAR_DAY_SELECTED_BG],
      color: colors[datePicker.CALENDAR_DAY_SELECTED_COLOR],

      '&:hover': {
        backgroundColor: colors[datePicker.CALENDAR_DAY_SELECTED_BG],
        color: colors[datePicker.CALENDAR_DAY_SELECTED_COLOR],
      },

      '&:focus': {
        backgroundColor: colors[datePicker.CALENDAR_DAY_SELECTED_BG],
        color: colors[datePicker.CALENDAR_DAY_SELECTED_COLOR],
      },
    },

    '& .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle':
      {
        borderBottomColor: colors.NEUTRAL_WHITE,
      },

    '& .react-datepicker__navigation': {
      width: datePicker.CALENDAR_NAVIGATION_SIZE,
      height: datePicker.CALENDAR_NAVIGATION_SIZE,
      top: datePicker.CALENDAR_NAVIGATION_OFFSET,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },

    '& .react-datepicker__navigation--next': {
      right: datePicker.CALENDAR_NAVIGATION_OFFSET,
      backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4NCjxwYXRoIGQ9Ik0xMSA4TDYuMDAwMDUgMTNMNS4zMDAwNSAxMi4zTDkuNjAwMDUgOEw1LjMwMDA1IDMuN0w2LjAwMDA1IDNMMTEgOFoiIGZpbGw9IiM1MjUyNTIiLz4NCjwvc3ZnPg==)',
    },

    '& .react-datepicker__navigation--previous': {
      left: datePicker.CALENDAR_NAVIGATION_OFFSET,
      backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4NCjxwYXRoIGQ9Ik01IDhMMTAgM0wxMC43IDMuN0w2LjQgOEwxMC43IDEyLjNMMTAgMTNMNSA4WiIgZmlsbD0iIzUyNTI1MiIvPg0KPC9zdmc+)',
    },

    '& .react-datepicker__navigation .react-datepicker__navigation-icon': {
      display: 'none',
    },

    '& .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button)':
      {
        right: 95,
      },

    '& .react-datepicker__navigation:focus': {
      outline: 'none',
    },

    '& .react-datepicker__navigation--years-upcoming': {
      backgroundImage: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4NCjxwYXRoIGQ9Ik00IDEwTDggNUwxMiAxMEg0WiIgZmlsbD0iIzE2MTYxNiIvPg0KPC9zdmc+")',
    },

    '& .react-datepicker__navigation--years-previous': {
      backgroundImage: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4NCjxwYXRoIGQ9Ik0xMiA2TDggMTFMNCA2SDEyWiIgZmlsbD0iIzE2MTYxNiIvPg0KPC9zdmc+")',
    },

    '& .react-datepicker__navigation--years-previous, & .react-datepicker__navigation--years-upcoming': {
      height: 20,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      top: 0,
    },
  }),
);

export const StyledIcon = styled.div<{
  isDisabled: boolean;
}>(({
  isDisabled,

  theme: {
    datePicker,
    colors,
    input,
  },
}) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  top: 0,
  right: 12,
  pointerEvents: 'none',
  width: 16,
  color: isDisabled
    ? colors[input.ICON_COLOR_DISABLED]
    : colors[datePicker.CALENDAR_ICON_COLOR],
}));
